import Phaser from 'phaser';
import { audio } from '../game/AudioManager.js';
import { gameState } from '../game/GameState.js';
import { questManager } from '../game/QuestManager.js';
import { promptManager, enemyBarManager, cinematicManager } from '../game/UIManager.js';
import { requestMobileFullscreen } from '../game/MobileFullscreenManager.js';

export class MainMenuScene extends Phaser.Scene {
  constructor() {
    super('MainMenuScene');
  }

  create() {
    questManager.show(false);
    promptManager.hide();
    enemyBarManager.hide();
    cinematicManager.hide();
    audio.playTheme('menu');

    const { width, height } = this.scale;

    // 1. Brand New Cinematic Metro of Hearts Start Wallpaper
    const bg = this.add.image(0, 0, 'bg_start_wallpaper')
      .setOrigin(0, 0)
      .setDisplaySize(width, height)
      .setDepth(0);

    // 2. Romantic Atmospheric Shading
    // Enhances contrast so the glowing title and menu buttons pop with brilliant clarity
    const overlay = this.add.graphics().setDepth(1);
    overlay.fillGradientStyle(0x190826, 0x190826, 0x10051a, 0x10051a, 0.45, 0.45, 0.3, 0.3);
    overlay.fillRect(0, 0, width, height);

    // Soft warm radial center glow
    const centerGlow = this.add.graphics().setDepth(2);
    centerGlow.fillStyle(0xffd166, 0.08);
    centerGlow.fillCircle(width / 2, height * 0.35, 260);

    // 3. Floating Romantic Rose Petals
    const particles = this.add.particles(0, 0, 'petal_particle', {
      x: { min: 0, max: width },
      y: 0,
      lifespan: 8000,
      speedY: { min: 35, max: 80 },
      speedX: { min: -18, max: 18 },
      scale: { start: 1.2, end: 0.5 },
      rotate: { min: 0, max: 360 },
      alpha: { start: 0.85, end: 0.15 },
      quantity: 1,
      frequency: 240
    }).setDepth(5);

    // 4. Main Title with Radiant Glow
    const titleText = this.add.text(width / 2, height * 0.22, "TARONNO'S LOVE", {
      fontFamily: 'Georgia, serif',
      fontSize: '64px',
      color: '#ff70a6',
      fontStyle: 'bold',
      shadow: { color: '#ff2a75', blur: 32, fill: true }
    }).setOrigin(0.5).setDepth(10);

    const tagline = this.add.text(width / 2, height * 0.33, '"A story ten years in the making."', {
      fontFamily: 'Georgia, serif',
      fontSize: '18px',
      color: '#fdf2f8',
      fontStyle: 'italic',
      shadow: { color: '#000000', blur: 10, fill: true }
    }).setOrigin(0.5).setDepth(10);

    // Grounded floor line on the balcony terrace deck
    const floorY = 502;
    const talhaX = width / 2 - 275;
    const emilyX = width / 2 + 275;

    // Contact shadows directly beneath their feet on the wooden planks
    const shadowTalha = this.add.ellipse(talhaX, floorY + 1, 48, 12, 0x000000, 0.55).setDepth(9);
    const shadowEmily = this.add.ellipse(emilyX, floorY + 1, 48, 12, 0x000000, 0.55).setDepth(9);

    // Character sprites standing firmly on the balcony deck floor flanking the menu
    // Origin Y is 240/256 (0.9375) so the actual feet soles rest directly on floorY
    const previewTalha = this.add.sprite(talhaX, floorY, 'talha_sheet', 0)
      .setScale(0.45)
      .setOrigin(0.5, 0.9375)
      .setDepth(10)
      .play('talha_idle');

    const previewEmily = this.add.sprite(emilyX, floorY, 'emily_sheet', 0)
      .setScale(0.45)
      .setOrigin(0.5, 0.9375)
      .setFlipX(true)
      .setDepth(10)
      .play('emily_idle');

    // Menu Buttons Container & Frosted Card Backdrop
    const menuItems = [
      { text: 'NEW GAME', action: () => this.startNewGame() },
      { text: 'CHAPTER SELECT', action: () => this.showChapterSelect() },
      { text: 'MEMORIES (10 YEARS)', action: () => this.showMemories() },
      { text: 'CREDITS', action: () => this.showCredits() }
    ];

    let startY = height * 0.47;

    const menuCard = this.add.rectangle(width / 2, startY + 74, 380, 220, 0x12061e, 0.78)
      .setStrokeStyle(1.5, 0xff70a6, 0.85)
      .setDepth(8);

    const cardGlow = this.add.rectangle(width / 2, startY + 74, 384, 224)
      .setStrokeStyle(1, 0xffd166, 0.4)
      .setDepth(8);

    menuItems.forEach((item, index) => {
      const btn = this.add.text(width / 2, startY + index * 48, item.text, {
        fontFamily: 'Trebuchet MS, Arial, sans-serif',
        fontSize: '20px',
        color: '#fdf2f8',
        fontStyle: 'bold',
        backgroundColor: 'rgba(38, 14, 52, 0.75)',
        padding: { x: 26, y: 8 }
      }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(12);

      btn.on('pointerover', () => {
        btn.setStyle({ color: '#ffd166', backgroundColor: 'rgba(255, 112, 166, 0.35)' });
        btn.setScale(1.04);
        audio.playClick();
      });

      btn.on('pointerout', () => {
        btn.setStyle({ color: '#fdf2f8', backgroundColor: 'rgba(38, 14, 52, 0.75)' });
        btn.setScale(1.0);
      });

      btn.on('pointerdown', item.action);
    });
  }

  startNewGame() {
    audio.playRomanticChime();
    requestMobileFullscreen();
    this.cameras.main.fade(800, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('PrologueScene');
    });
  }

  showChapterSelect() {
    audio.playClick();
    const { width, height } = this.scale;

    const modal = this.add.container(0, 0).setDepth(100);

    // Fullscreen dark dimmer backdrop - absorbs all clicks so nothing underneath can be clicked or seen through
    const dimmer = this.add.rectangle(width / 2, height / 2, width, height, 0x07020d, 0.88)
      .setInteractive();
    modal.add(dimmer);

    // Outer Glow & Golden Border Card
    const cardW = 600;
    const cardH = 430;
    const cardGlow = this.add.rectangle(width / 2, height / 2, cardW + 6, cardH + 6)
      .setStrokeStyle(1.5, 0xffd166, 0.5);
    const bg = this.add.rectangle(width / 2, height / 2, cardW, cardH, 0x14061e, 0.98)
      .setStrokeStyle(2, 0xff70a6, 0.9);
    modal.add([cardGlow, bg]);

    // Modal Header
    const title = this.add.text(width / 2, height / 2 - 165, '📖 CHAPTER SELECT', {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '24px',
      color: '#ffd166',
      fontStyle: 'bold',
      shadow: { color: '#ffd166', blur: 12, fill: true }
    }).setOrigin(0.5);

    const subTitle = this.add.text(width / 2, height / 2 - 138, 'Choose a chapter to replay the story', {
      fontFamily: 'Georgia, serif',
      fontSize: '13px',
      color: '#fdf2f8',
      fontStyle: 'italic'
    }).setOrigin(0.5);

    modal.add([title, subTitle]);

    const chapters = [
      { name: 'Prologue: The Boy Who Never Turned (2011)', scene: 'PrologueScene' },
      { name: 'Level 1: The School Road & Rooftop (2011)', scene: 'Level1Scene' },
      { name: 'Level 2: Found Again on FaceSpace (2016)', scene: 'Level2Scene' },
      { name: 'Memories: Ten Years of Love (2016—2026)', scene: 'MontageScene' },
      { name: '⚔️ Level 3: The 3 Knight Battles (Save Queen Labonno)', scene: 'Level3Scene' },
      { name: 'Finale: Garden of Forever (Together Always)', scene: 'GardenEndingScene' }
    ];

    chapters.forEach((ch, idx) => {
      const btnY = height / 2 - 95 + idx * 46;
      const btn = this.add.text(width / 2, btnY, ch.name, {
        fontFamily: 'Trebuchet MS, Arial, sans-serif',
        fontSize: '16px',
        color: '#fdf2f8',
        backgroundColor: 'rgba(40, 14, 56, 0.85)',
        padding: { x: 20, y: 8 }
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });

      btn.on('pointerover', () => {
        btn.setStyle({ color: '#ffd166', backgroundColor: 'rgba(255, 112, 166, 0.45)' });
        btn.setScale(1.03);
        audio.playClick();
      });

      btn.on('pointerout', () => {
        btn.setStyle({ color: '#fdf2f8', backgroundColor: 'rgba(40, 14, 56, 0.85)' });
        btn.setScale(1.0);
      });

      btn.on('pointerdown', () => {
        audio.playClick();
        requestMobileFullscreen();
        modal.destroy();
        this.scene.start(ch.scene);
      });

      modal.add(btn);
    });

    // Close Button
    const closeBtn = this.add.text(width / 2, height / 2 + 180, '✕ CLOSE', {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '15px',
      fontWeight: 'bold',
      color: '#fdf2f8',
      backgroundColor: 'rgba(244, 63, 94, 0.8)',
      padding: { x: 22, y: 6 }
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    closeBtn.on('pointerover', () => {
      closeBtn.setStyle({ color: '#ffd166', backgroundColor: 'rgba(244, 63, 94, 1)' });
      closeBtn.setScale(1.05);
      audio.playClick();
    });

    closeBtn.on('pointerout', () => {
      closeBtn.setStyle({ color: '#fdf2f8', backgroundColor: 'rgba(244, 63, 94, 0.8)' });
      closeBtn.setScale(1.0);
    });

    closeBtn.on('pointerdown', () => {
      audio.playClick();
      modal.destroy();
    });

    modal.add(closeBtn);

    // Entrance pop tween
    modal.setScale(0.92);
    modal.setAlpha(0);
    this.tweens.add({
      targets: modal,
      scale: 1,
      alpha: 1,
      duration: 200,
      ease: 'Back.easeOut'
    });
  }

  showMemories() {
    requestMobileFullscreen();
    this.scene.start('MontageScene');
  }

  showCredits() {
    audio.playClick();
    const { width, height } = this.scale;

    const modal = this.add.container(0, 0).setDepth(100);

    const dimmer = this.add.rectangle(width / 2, height / 2, width, height, 0x07020d, 0.88)
      .setInteractive();
    modal.add(dimmer);

    const cardW = 540;
    const cardH = 340;
    const cardGlow = this.add.rectangle(width / 2, height / 2, cardW + 6, cardH + 6)
      .setStrokeStyle(1.5, 0xffd166, 0.5);
    const bg = this.add.rectangle(width / 2, height / 2, cardW, cardH, 0x14061e, 0.98)
      .setStrokeStyle(2, 0xff70a6, 0.9);
    modal.add([cardGlow, bg]);

    const title = this.add.text(width / 2, height / 2 - 110, "💖 TARONNO'S LOVE", {
      fontFamily: 'Georgia, serif',
      fontSize: '28px',
      color: '#ff70a6',
      fontStyle: 'bold',
      shadow: { color: '#ff2a75', blur: 16, fill: true }
    }).setOrigin(0.5);

    const sub = this.add.text(width / 2, height / 2 - 70, '10 Years in the Making', {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '15px',
      color: '#ffd166',
      fontWeight: 'bold'
    }).setOrigin(0.5);

    const desc = this.add.text(width / 2, height / 2 - 10, 'A deeply personal romantic RPG celebration of\nTalha & Labonno (2016 — 2026).\n\n"From a glance across the schoolyard,\nto a love that conquers all worlds."', {
      fontFamily: 'Georgia, serif',
      fontSize: '15px',
      color: '#fdf2f8',
      align: 'center',
      lineSpacing: 6
    }).setOrigin(0.5);

    const closeBtn = this.add.text(width / 2, height / 2 + 115, '✕ CLOSE', {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '15px',
      fontWeight: 'bold',
      color: '#fdf2f8',
      backgroundColor: 'rgba(244, 63, 94, 0.8)',
      padding: { x: 22, y: 6 }
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    closeBtn.on('pointerover', () => {
      closeBtn.setStyle({ color: '#ffd166', backgroundColor: 'rgba(244, 63, 94, 1)' });
      closeBtn.setScale(1.05);
      audio.playClick();
    });

    closeBtn.on('pointerout', () => {
      closeBtn.setStyle({ color: '#fdf2f8', backgroundColor: 'rgba(244, 63, 94, 0.8)' });
      closeBtn.setScale(1.0);
    });

    closeBtn.on('pointerdown', () => {
      audio.playClick();
      modal.destroy();
    });

    modal.add([title, sub, desc, closeBtn]);

    modal.setScale(0.92);
    modal.setAlpha(0);
    this.tweens.add({
      targets: modal,
      scale: 1,
      alpha: 1,
      duration: 200,
      ease: 'Back.easeOut'
    });
  }
}
